import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Eye,
  List,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { utilService } from '../services/util';
import { Session } from '../types/api';

const EASTERN_TIMEZONE = 'America/New_York';

const getZonedParts = (date: Date, timeZone: string) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const partMap = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    year: Number(partMap.year),
    month: Number(partMap.month),
    day: Number(partMap.day),
    hour: Number(partMap.hour),
    minute: Number(partMap.minute),
    second: Number(partMap.second),
  };
};

const getOffsetMinutes = (date: Date, timeZone: string) => {
  const zoned = getZonedParts(date, timeZone);
  const utcFromZoned = Date.UTC(
    zoned.year,
    zoned.month - 1,
    zoned.day,
    zoned.hour,
    zoned.minute,
    zoned.second
  );
  return Math.round((utcFromZoned - date.getTime()) / 60000);
};

const formatIsoInTimezone = (date: Date, timeZone: string): string => {
  const zoned = getZonedParts(date, timeZone);
  const offsetMinutes = getOffsetMinutes(date, timeZone);
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absoluteMinutes / 60)).padStart(2, '0');
  const offsetMins = String(absoluteMinutes % 60).padStart(2, '0');
  return `${zoned.year}-${String(zoned.month).padStart(2, '0')}-${String(zoned.day).padStart(2, '0')}T${String(zoned.hour).padStart(2, '0')}:${String(zoned.minute).padStart(2, '0')}:${String(zoned.second).padStart(2, '0')}${sign}${offsetHours}:${offsetMins}`;
};

const getDateKeyInTimezone = (dateString: string, timeZone: string): string => {
  const date = new Date(dateString);
  const zoned = getZonedParts(date, timeZone);
  return `${zoned.year}-${String(zoned.month).padStart(2, '0')}-${String(zoned.day).padStart(2, '0')}`;
};

const getDayBoundaryIsoInEastern = (year: number, month: number, day: number, isEndOfDay: boolean) => {
  const boundary = isEndOfDay
    ? new Date(year, month - 1, day, 23, 59, 59)
    : new Date(year, month - 1, day, 0, 0, 0);
  return formatIsoInTimezone(boundary, EASTERN_TIMEZONE);
};

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const calendarDays = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startWeekday = firstDay.getDay();
    const days: Array<{ date: Date; inCurrentMonth: boolean }> = [];

    for (let i = startWeekday - 1; i >= 0; i -= 1) {
      days.push({ date: new Date(year, month, -i), inCurrentMonth: false });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      days.push({ date: new Date(year, month, day), inCurrentMonth: true });
    }

    while (days.length % 7 !== 0) {
      const lastDate = days[days.length - 1].date;
      days.push({
        date: new Date(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate() + 1),
        inCurrentMonth: false,
      });
    }

    return days;
  }, [selectedMonth]);

  const calendarRangeStart = useMemo(() => (
    new Date(
      calendarDays[0].date.getFullYear(),
      calendarDays[0].date.getMonth(),
      calendarDays[0].date.getDate(),
      0,
      0,
      0
    )
  ), [calendarDays]);

  const calendarRangeEnd = useMemo(() => {
    const last = calendarDays[calendarDays.length - 1].date;
    return new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 59, 59);
  }, [calendarDays]);

  const isSelectedMonthCurrentOrFuture = useMemo(() => {
    const now = new Date();
    return (
      selectedMonth.getFullYear() > now.getFullYear()
      || (selectedMonth.getFullYear() === now.getFullYear() && selectedMonth.getMonth() >= now.getMonth())
    );
  }, [selectedMonth]);

  // Fetch sessions for the full displayed calendar grid (including leading/trailing days).
  // The API caps each request at 100; page through until the whole range is loaded.
  const { data: sessionsData, isLoading: sessionsLoading } = useQuery(
    ['dashboard-calendar-sessions', selectedMonth.getFullYear(), selectedMonth.getMonth()],
    async () => {
      const pageLimit = 100;
      const baseParams = {
        startDate: formatIsoInTimezone(calendarRangeStart, EASTERN_TIMEZONE),
        endDate: formatIsoInTimezone(calendarRangeEnd, EASTERN_TIMEZONE),
        valid: true,
        limit: pageLimit,
      };

      const firstPage = await apiService.getSessions({ ...baseParams, page: 1 });
      const totalPages = Math.max(1, firstPage.meta?.totalPages ?? 1);
      const sessions = [...(firstPage.sessions || [])];

      if (totalPages > 1) {
        const remainingPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) =>
            apiService.getSessions({ ...baseParams, page: index + 2 })
          )
        );
        remainingPages.forEach((page) => {
          sessions.push(...(page.sessions || []));
        });
      }

      return {
        sessions,
        meta: {
          ...firstPage.meta,
          total: firstPage.meta?.total ?? sessions.length,
          page: 1,
          limit: pageLimit,
          totalPages,
        },
      };
    },
    { refetchInterval: 300_000 } // Refetch every 5 minutes
  );

  const sessionsByDay = useMemo(() => {
    const grouped = new Map<string, Session[]>();
    (sessionsData?.sessions || []).forEach((session) => {
      const key = getDateKeyInTimezone(session.start, EASTERN_TIMEZONE);
      const existing = grouped.get(key) || [];
      existing.push(session);
      grouped.set(key, existing);
    });

    grouped.forEach((daySessions, key) => {
      grouped.set(
        key,
        [...daySessions].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
      );
    });

    return grouped;
  }, [sessionsData]);

  const mobileSessionDayGroups = Array.from(sessionsByDay.entries())
    .filter(([, daySessions]) => daySessions.length > 0)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, daySessions]) => ({
      key,
      sessions: [...daySessions].sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()),
    }));

  return (
    <div className="space-y-8">
      <div className="hidden md:block bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">
          Welcome back, {user?.fullName || user?.email}!
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(320px,1fr)] gap-8">
        <div className="card order-1 lg:order-2">
          <h2 className="text-xl font-semibold text-secondary-900 mb-6">Quick Actions</h2>
          <div className="space-y-4">
            <button
              onClick={() => navigate('/headset-pairing')}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Eye className="h-5 w-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-white">Pair New Headset</p>
                  <p className="text-sm text-primary-100">Connect a new VR device</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-white" />
            </button>

            <button
              onClick={() => navigate('/available-sessions')}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <List className="h-5 w-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-white">Create a Session Code</p>
                  <p className="text-sm text-primary-100">Generate a code for use on any headset</p>
                </div>
              </div>

              <ArrowRight className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        <div className="card order-2 lg:order-1">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-secondary-900">Sessions</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Previous month"
                onClick={() => {
                  setSelectedMonth((previous) => new Date(previous.getFullYear(), previous.getMonth() - 1, 1));
                }}
                className="p-2 rounded-md border border-secondary-200 hover:bg-secondary-50"
              >
                <ChevronLeft className="h-4 w-4 text-secondary-700" />
              </button>
              <span className="text-sm font-semibold text-secondary-800 min-w-[100px] text-center">
                {selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <button
                type="button"
                aria-label="Next month"
                disabled={isSelectedMonthCurrentOrFuture}
                onClick={() => {
                  setSelectedMonth((previous) => new Date(previous.getFullYear(), previous.getMonth() + 1, 1));
                }}
                className="p-2 rounded-md border border-secondary-200 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <ChevronRight className="h-4 w-4 text-secondary-700" />
              </button>
            </div>
          </div>

          {sessionsLoading ? (
            <div className="space-y-3">
              <div className="hidden lg:grid grid-cols-7 gap-2">
                {[...Array(7)].map((_, i) => (
                  <div key={i} className="h-4 bg-secondary-200 rounded animate-pulse" />
                ))}
              </div>
              <div className="hidden lg:grid grid-cols-7 gap-2">
                {[...Array(35)].map((_, i) => (
                  <div key={i} className="h-28 bg-secondary-100 rounded animate-pulse" />
                ))}
              </div>
              <div className="lg:hidden space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 bg-secondary-100 rounded animate-pulse" />
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="hidden lg:block space-y-2">
                <div className="grid grid-cols-7 gap-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayLabel) => (
                    <div key={dayLabel} className="text-xs font-semibold text-secondary-500 text-center">
                      {dayLabel}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {calendarDays.map(({ date, inCurrentMonth }) => {
                    const year = date.getFullYear();
                    const month = date.getMonth() + 1;
                    const day = date.getDate();
                    const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const daySessions = sessionsByDay.get(key) || [];
                    const visibleSessions = daySessions.slice(0, 3);
                    const hasOverflow = daySessions.length > 3;

                    const blockHeight = hasOverflow
                      ? '28%'
                      : daySessions.length === 1
                        ? '100%'
                        : daySessions.length === 2
                          ? '50%'
                          : daySessions.length === 3
                            ? '33.3333%'
                            : '0%';

                    return (
                      <div
                        key={key}
                        className={`rounded-lg border p-2 h-36 ${inCurrentMonth ? 'bg-white' : 'bg-secondary-50'} ${daySessions.length > 0 ? 'border-primary-300 bg-primary-50/40' : 'border-secondary-200'}`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            const startDate = getDayBoundaryIsoInEastern(year, month, day, false);
                            const endDate = getDayBoundaryIsoInEastern(year, month, day, true);
                            navigate(`/sessions?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`);
                          }}
                          className={`text-xs font-semibold mb-1 cursor-pointer ${inCurrentMonth ? 'text-secondary-800' : 'text-secondary-400'}`}
                        >
                          {day}
                        </button>
                        <div className="h-[calc(100%-20px)]">
                          {daySessions.length > 0 && (
                            <div className="h-full flex flex-col gap-1">
                              {visibleSessions.map((session) => (
                                <button
                                  key={session.id}
                                  type="button"
                                  style={{ height: blockHeight }}
                                  onClick={() => navigate(`/sessions/${session.id}`)}
                                  className="w-full text-left rounded bg-primary-100 hover:bg-primary-200 px-2 py-1 overflow-hidden"
                                  title={`${utilService.sessionName(session)} - ${new Date(session.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
                                >
                                  <p className="text-[10px] leading-tight font-medium text-primary-900 truncate">
                                    {utilService.sessionName(session)}
                                  </p>
                                  <p className="text-[10px] leading-tight text-primary-700">
                                    {new Date(session.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                  </p>
                                </button>
                              ))}
                              {hasOverflow && (
                                <button
                                  type="button"
                                  style={{ height: '16%' }}
                                  onClick={() => {
                                    const startDate = getDayBoundaryIsoInEastern(year, month, day, false);
                                    const endDate = getDayBoundaryIsoInEastern(year, month, day, true);
                                    navigate(`/sessions?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`);
                                  }}
                                  className="text-[10px] text-primary-700 hover:text-primary-800 hover:bg-secondary-100 text-right px-1 rounded"
                                >
                                  More...
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="lg:hidden space-y-4">
                {mobileSessionDayGroups.length === 0 ? (
                  <p className="text-sm text-secondary-500">No sessions in this month.</p>
                ) : (
                  mobileSessionDayGroups.map((group) => (
                    <div key={group.key} className="border border-secondary-200 rounded-lg p-3">
                      <h3 className="font-semibold text-secondary-900 mb-2">
                        {new Date(`${group.key}T00:00:00`).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </h3>
                      <ol className="space-y-2">
                        {group.sessions.map((session, index) => (
                          <li key={session.id}>
                            <button
                              type="button"
                              onClick={() => navigate(`/sessions/${session.id}`)}
                              className="w-full text-left p-2 rounded bg-secondary-50 hover:bg-secondary-100"
                            >
                              <p className="text-sm font-medium text-secondary-900">{utilService.sessionName(session)}</p>
                              <p className="text-xs text-secondary-600">
                                {new Date(session.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                              </p>
                            </button>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
